import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import EvidenceBadge, { EVIDENCE_OPTIONS } from "@/components/EvidenceBadge";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Housing Authority","CoC / Coordinator","VA","Nonprofit / SSVF","Nonprofit","Behavioral Health LMHA","Government","Operator","Funder"];
const COUNTIES = [
  "Bexar","Comal","Guadalupe","Kendall",
  "Travis","Williamson","Hays","Bastrop","Caldwell",
  "Harris","Fort Bend","Montgomery","Brazoria","Galveston",
  "Dallas","Tarrant","Collin","Denton","Rockwall",
];

export default function Agencies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name:"", type:"Nonprofit", counties:[], website:"", notes:"", evidence_level:"public_source" });

  const load = async () => setItems((await api.get("/agencies")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    await api.post("/agencies", form);
    toast.success("Agency added"); setOpen(false); load();
  };
  const toggleCounty = c => setForm(f => ({...f, counties: f.counties.includes(c) ? f.counties.filter(x=>x!==c) : [...f.counties, c]}));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Directory</div>
          <h1 className="text-3xl font-bold">Agencies & Partners</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-agency-btn"><Plus className="w-4 h-4 mr-1"/>Add Agency</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Agency</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="ag-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v=>setForm({...form, type:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Counties</Label>
                <div className="flex gap-2 flex-wrap mt-1">
                  {COUNTIES.map(c => (
                    <button type="button" key={c} onClick={()=>toggleCounty(c)}
                      className={`px-3 py-1 rounded-full text-xs border ${form.counties.includes(c) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>{c}</button>
                  ))}
                </div>
              </div>
              <div><Label>Website</Label><Input value={form.website} onChange={e=>setForm({...form, website:e.target.value})}/></div>
              <div><Label>Evidence Level</Label>
                <Select value={form.evidence_level} onValueChange={v=>setForm({...form, evidence_level:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{EVIDENCE_OPTIONS.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
              <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="ag-submit">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 border-slate-200 flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search agencies by name, type, county..." className="pl-9" value={q} onChange={e=>setQ(e.target.value)} data-testid="ag-search"/>
        </div>
        <div className="text-xs text-slate-500 font-mono">
          {items.filter(a => q==="" || `${a.name} ${a.type} ${(a.counties||[]).join(" ")} ${a.notes||""}`.toLowerCase().includes(q.toLowerCase())).length} shown
        </div>
      </Card>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.filter(a => q==="" || `${a.name} ${a.type} ${(a.counties||[]).join(" ")} ${a.notes||""}`.toLowerCase().includes(q.toLowerCase())).map(a => (
          <Card key={a.id} className="p-5 border-slate-200" data-testid={`agency-card-${a.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{a.type}</div>
              <EvidenceBadge level={a.evidence_level} />
            </div>
            <h3 className="font-semibold text-slate-900">{a.name}</h3>
            <div className="text-xs text-slate-600 mt-1">Counties: {(a.counties||[]).join(", ") || "—"}</div>
            {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="text-xs text-[#C25E00] hover:underline">{a.website}</a>}
            {a.notes && <p className="text-xs text-slate-500 mt-2">{a.notes}</p>}
          </Card>
        ))}
      </div>
    </div>
  );
}
